import React from "react";
import {
    Box,
    Typography,
    Grid,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Table,
    TableRow,
    Card,
    CardContent,
    Chip,
} from "@mui/material";

// Common styles configuration
const STYLES = {
    colors: {
        primary: "#000088",
        secondary: "#262655",
        accent: "#4A3D9E",
        selected: "#F5F5FA",
        textPrimary: "#1A1A1A",
        textSecondary: "#888",
        textTertiary: "#999",
        border: "#D5D9EB",
        borderLight: "#E3E6F0",
        borderDashed: "#CFCFCF",
        background: "#F3FBFF",
        backgroundWhite: "#FFFFFF",
        backgroundHover: "#F9FBFF",
        buttonHover: "#FFF",
        skeleton: "#E3E6F0",
        skeletonWave:
            "linear-gradient(90deg, #E3E6F0 40%, #F9FBFF 60%, #E3E6F0 80%)",
        icon: "#B0B0B0",
    },
    typography: {
        fontFamily: "Poppins, sans-serif",
        title: {
            fontSize: { xs: "20px", sm: "24px" },
            fontWeight: 700,
        },
        subtitle: {
            fontSize: { xs: "16px", sm: "18px" },
        },
        body: {
            fontSize: 14,
            fontWeight: 500,
        },
        tableHeader: {
            fontSize: { xs: "12px", sm: "14px" },
            fontWeight: 600,
        },
        tableCell: {
            fontSize: { xs: "11px", sm: "12px" },
            fontWeight: 400,
        },
        caption: {
            fontSize: 11,
            fontWeight: 500,
        },
    },
    spacing: {
        xs: 2,
        sm: 3,
        md: 3,
        lg: 4,
        xl: 4,
    },
    borderRadius: {
        card: "16px",
        button: "20px",
        select: "8px",
        swiperCard: "12px",
        small: "6px",
    },
    boxShadow: {
        card: "0 2px 8px 0 rgba(0,0,0,0.1)",
        cardHover: "0 4px 16px 0 rgba(0,0,0,0.15)",
        container: "0 2px 8px 0 rgba(47,40,118,0.08)",
    },
    borders: {
        primary: "1px solid #000088",
        secondary: "1px solid #E3E6F0",
        dashed: "1px dashed #CFCFCF",
        select: "1px solid #D5D9EB",
        button: "1px solid rgba(74,61,158,1)",
    },
};

function LearnAndEarnSuggestion({
    chatGPTData,
    darkMode = false,
}) {
    // Dark theme styles
    const darkStyles = darkMode ? {
        background: "transparent",
        textPrimary: "#fff",
        textSecondary: "rgba(255, 255, 255, 0.8)",
        textTertiary: "rgba(255, 255, 255, 0.6)",
        border: "rgba(255, 255, 255, 0.1)",
        tableHeaderBg: "rgba(255, 255, 255, 0.05)",
        tableRowBg: "transparent",
        tableRowHover: "rgba(102, 82, 228, 0.1)",
    } : {
        background: STYLES.colors.background,
        textPrimary: STYLES.colors.textPrimary,
        textSecondary: STYLES.colors.textSecondary,
        textTertiary: STYLES.colors.textTertiary,
        border: STYLES.borders.secondary,
        tableHeaderBg: "#F5F5F5",
        tableRowBg: "#FFFFFF",
        tableRowHover: "rgba(0, 0, 136, 0.02)",
    };

    return (
        <Box
            id="learn-earn"
            sx={{
                borderRadius: STYLES.borderRadius.card,
                background: darkStyles.background,
                boxSizing: "inherit",
            }}
            className="lear-earn-recommendations"
        >
            <Box>
                {chatGPTData?.latestTechnologies &&
                    Array.isArray(
                        chatGPTData?.latestTechnologies || chatGPTData
                    ) ? (
                    <Box>

                        {/* Desktop Table View */}
                        <Box sx={{ display: { xs: 'block', md: 'block' } }}>
                            <TableContainer sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table
                                    sx={{ 
                                        overflowX: "hidden", 
                                        maxWidth: "351px",
                                        '& .MuiTableCell-root': {
                                            padding: '8px 4px',
                                            borderBottom: darkMode 
                                                ? '1px solid rgba(255, 255, 255, 0.1)' 
                                                : '1px solid rgba(224, 224, 224, 0.3)',
                                            backgroundColor: 'transparent',
                                        },
                                        '& .MuiTableHead-root .MuiTableCell-root': {
                                            padding: '6px 4px',
                                            backgroundColor: darkStyles.tableHeaderBg,
                                        },
                                        '& .MuiTableRow-root': {
                                            backgroundColor: darkStyles.tableRowBg,
                                            '&:hover': {
                                                backgroundColor: darkStyles.tableRowHover,
                                            },
                                        },
                                    }}
                                    aria-label="simple table"
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center" sx={{ padding: '6px 4px' }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: STYLES.typography.fontFamily,
                                                        fontSize: { xs: "10px", sm: "12px" },
                                                        fontWeight: STYLES.typography.tableHeader.fontWeight,
                                                        textAlign: "center",
                                                        color: darkStyles.textPrimary,
                                                    }}
                                                >
                                                    Skill
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center" sx={{ padding: '6px 4px' }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: STYLES.typography.fontFamily,
                                                        fontSize: { xs: "10px", sm: "12px" },
                                                        fontWeight: STYLES.typography.tableHeader.fontWeight,
                                                        textAlign: "center",
                                                        color: darkStyles.textPrimary,
                                                    }}
                                                >
                                                    Ease of Learning
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center" sx={{ padding: '6px 4px' }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: STYLES.typography.fontFamily,
                                                        fontSize: { xs: "10px", sm: "12px" },
                                                        fontWeight: STYLES.typography.tableHeader.fontWeight,
                                                        textAlign: "center",
                                                        color: darkStyles.textPrimary,
                                                    }}
                                                >
                                                    Salary Increase
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {chatGPTData?.latestTechnologies?.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{ 
                                                        display: "flex", 
                                                        border: "none",
                                                        padding: '8px 4px',
                                                        alignItems: 'center',
                                                        backgroundColor: 'transparent',
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        sx={{
                                                            width: { xs: "12px", sm: "16px" },
                                                            height: { xs: "12px", sm: "16px" },
                                                        }}
                                                        loading="lazy"
                                                        src="../../assets/images/fire-icon.svg"
                                                        alt="fire"
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontFamily: STYLES.typography.fontFamily,
                                                            fontSize: { xs: "9px", sm: "11px" },
                                                            fontWeight: STYLES.typography.tableCell.fontWeight,
                                                            textAlign: "left",
                                                            paddingLeft: "6px",
                                                            color: darkStyles.textPrimary,
                                                        }}
                                                        component="span"
                                                    >
                                                        {item?.skillName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ 
                                                    border: "none",
                                                    padding: '8px 4px',
                                                    backgroundColor: 'transparent',
                                                }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: STYLES.typography.fontFamily,
                                                            fontSize: { xs: "9px", sm: "11px" },
                                                            fontWeight: STYLES.typography.tableCell.fontWeight,
                                                            textAlign: "center",
                                                            color: darkStyles.textSecondary,
                                                        }}
                                                    >
                                                        {item?.easeOfLearning}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ 
                                                    border: "none",
                                                    padding: '8px 4px',
                                                    backgroundColor: 'transparent',
                                                }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: STYLES.typography.fontFamily,
                                                            fontSize: { xs: "9px", sm: "11px" },
                                                            fontWeight: 600,
                                                            textAlign: "center",
                                                            color: darkMode ? "#6652E4" : darkStyles.textPrimary,
                                                        }}
                                                    >
                                                        {item?.potentialSalaryIncreasePercentage}%
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        textAlign="center"
                        sx={{ py: 4 }}
                    >
                        <Box>
                            <img
                                src="../../assets/images/empty-list.svg"
                                alt="data not found"
                                loading="lazy"
                                style={{ maxWidth: "120px" }}
                            />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                textAlign: "center",
                                fontSize: STYLES.typography.subtitle,
                                mt: 2,
                                color: darkStyles.textPrimary,
                            }}
                        >
                            Recommendations are not found
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default LearnAndEarnSuggestion;